import { useState, useEffect } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { toast } from 'sonner';
import { Plus, Settings, Check, User, Mail, Calendar, Key, Copy, MoreVertical, Search, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';

export default function Team() {
  const { apiFetch, user: currentUser } = useAdmin();
  const [users, setUsers] = useState([]);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [search, setSearch] = useState('');

  const loadData = async () => {
    try {
      const [usersRes, schemaRes] = await Promise.all([
        apiFetch('/auth/users'),
        apiFetch('/auth/permissions-schema')
      ]);
      const usersData = await usersRes.json();
      const schemaData = await schemaRes.json();
      setUsers(usersData.users || []);
      setSchema(schemaData || {});
    } catch (err) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFetch]);

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(search.toLowerCase()) || 
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  if (currentUser?.role !== 'owner') {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-center space-y-4">
        <AlertCircle className="w-12 h-12 text-red-500" />
        <h2 className="text-xl font-medium">Access Denied</h2>
        <p className="text-stone-500">Only workspace owners can manage the team.</p>
      </div>
    );
  }

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-64"/><Skeleton className="h-40 w-full rounded-xl"/></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-playfair font-medium text-stone-900">Team Management</h2>
          <p className="text-sm text-stone-500 mt-1">Manage staff access and permissions</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 w-4 h-4" />
            <Input 
              value={search} 
              onChange={e => setSearch(e.target.value)} 
              placeholder="Search team..." 
              className="pl-9 h-10 w-full bg-white border-stone-200" 
            />
          </div>
          <Button onClick={() => setInviteOpen(true)} className="h-10 bg-stone-900 text-white hover:bg-stone-800 gap-2 shrink-0">
            <Plus className="w-4 h-4" /> Invite Staff
          </Button>
        </div>
      </div>

      <div className="grid gap-4">
        {filteredUsers.map(u => (
          <UserCard key={u.id} user={u} onEdit={() => setEditingUser(u)} />
        ))}
        {filteredUsers.length === 0 && (
          <div className="text-center py-12 text-stone-500 text-sm bg-white rounded-xl border border-stone-200 border-dashed">
            No users found matching "{search}"
          </div>
        )}
      </div>

      <AnimatePresence>
        {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} onInvited={loadData} />}
        {editingUser && (
          <PermissionsPanel 
            user={editingUser} 
            schema={schema} 
            onClose={() => setEditingUser(null)} 
            onSaved={loadData}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function UserCard({ user, onEdit }) {
  return (
    <Card className="border-stone-200 shadow-sm overflow-hidden group">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 bg-white">
        <div className="h-12 w-12 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 font-bold shrink-0">
          {user.name.charAt(0)}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="font-medium text-stone-900 truncate">{user.name}</h3>
            <span className={cn(
              "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider",
              user.role === 'owner' ? 'bg-amber-100 text-amber-700' : 'bg-blue-50 text-blue-700'
            )}>
              {user.role}
            </span>
            {!user.isActive && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-50 text-red-700">
                Inactive
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-stone-500">
            <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {user.email}</span>
            {user.lastLoginAt ? (
              <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Last login {new Date(user.lastLoginAt).toLocaleDateString()}</span>
            ) : (
              <span className="flex items-center gap-1.5 italic"><Calendar className="w-3.5 h-3.5" /> Never logged in</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 mt-4 sm:mt-0">
          <div className="flex gap-1 mr-4">
            {user.siteAccess.map(s => (
              <span key={s} className="w-6 h-6 rounded flex items-center justify-center bg-stone-100 text-stone-600 text-[10px] font-bold border border-stone-200" title={s}>
                {s.split('_').map(w=>w[0].toUpperCase()).join('')}
              </span>
            ))}
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onEdit} 
            disabled={user.role === 'owner'}
            className="h-9 gap-2 whitespace-nowrap"
          >
            <Settings className="w-3.5 h-3.5" /> Permissions
          </Button>
        </div>
      </div>
    </Card>
  );
}

function InviteModal({ onClose, onInvited }) {
  const { apiFetch } = useAdmin();
  const [form, setForm] = useState({ name: '', email: '', role: 'staff', siteAccess: ['shihab_romeed'] });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const toggleSite = (k) => setForm(p => ({
    ...p, 
    siteAccess: p.siteAccess.includes(k) ? p.siteAccess.filter(s=>s!==k) : [...p.siteAccess, k]
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiFetch('/auth/invite', { method: 'POST', body: form });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setResult({ user: data.user, tempPassword: data.tempPassword });
      onInvited();
      toast.success('User invited');
    } catch (err) {
      toast.error(err.message || 'Failed to invite');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="p-6 border-b border-stone-100 flex items-center justify-between">
          <h3 className="text-lg font-playfair font-medium">Invite Staff</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600"><Plus className="w-5 h-5 rotate-45" /></button>
        </div>
        
        {result ? (
          <div className="p-6 space-y-6">
            <div className="bg-emerald-50 text-emerald-800 p-4 rounded-xl border border-emerald-100 text-sm">
              User created successfully. They can log in with the following credentials.
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-stone-500 mb-1.5">Email</p>
                <div className="flex items-center gap-2 p-3 bg-stone-50 rounded-lg border border-stone-200">
                  <span className="flex-1 text-sm font-medium">{result.user.email}</span>
                  <button onClick={()=>copyToClipboard(result.user.email)} className="text-stone-400 hover:text-stone-900"><Copy className="w-4 h-4"/></button>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-stone-500 mb-1.5">Temporary Password</p>
                <div className="flex items-center gap-2 p-3 bg-stone-50 rounded-lg border border-stone-200">
                  <span className="flex-1 text-sm font-mono tracking-wider">{result.tempPassword}</span>
                  <button onClick={()=>copyToClipboard(result.tempPassword)} className="text-stone-400 hover:text-stone-900"><Copy className="w-4 h-4"/></button>
                </div>
              </div>
            </div>
            <Button onClick={onClose} className="w-full bg-stone-900 text-white">Done</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-stone-700">Name</label>
              <Input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="h-10" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-stone-700">Email</label>
              <Input type="email" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})} className="h-10" />
            </div>
            <div className="space-y-3 pt-2">
              <label className="text-xs font-medium text-stone-700">Site Access</label>
              <div className="flex flex-wrap gap-2">
                {[
                  {k:'shihab_romeed', l:'Shihab Romeed'},
                  {k:'specialist_dental_london', l:'Specialist Dental London'},
                  {k:'london_dental_academy', l:'London Dental Academy'}
                ].map(s => (
                  <button 
                    type="button"
                    key={s.k}
                    onClick={() => toggleSite(s.k)}
                    className={cn(
                      "px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors",
                      form.siteAccess.includes(s.k) 
                        ? "bg-stone-900 border-stone-900 text-white" 
                        : "bg-white border-stone-200 text-stone-600 hover:border-stone-300"
                    )}
                  >
                    {s.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="pt-4 flex gap-3">
              <Button type="submit" disabled={saving || !form.name || !form.email || form.siteAccess.length===0} className="flex-1 bg-stone-900 text-white h-11">
                {saving ? 'Creating...' : 'Create Account'}
              </Button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}

function PermissionsPanel({ user, schema, onClose, onSaved }) {
  const { apiFetch } = useAdmin();
  const [perms, setPerms] = useState(() => user.permissions || {});
  const [saving, setSaving] = useState(false);

  const togglePerm = (resource, action) => {
    setPerms(prev => {
      const next = { ...prev };
      if (!next[resource]) next[resource] = {};
      next[resource] = { ...next[resource], [action]: !next[resource][action] };
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/auth/users/${user.id}/permissions`, { method: 'PUT', body: { permissions: perms } });
      if (!res.ok) throw new Error('Failed to save permissions');
      toast.success('Permissions updated');
      onSaved();
      onClose();
    } catch(err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      <motion.div 
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 right-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl border-l border-stone-200 z-50 flex flex-col"
      >
        <div className="p-6 border-b border-stone-200 flex items-center justify-between shrink-0 bg-stone-50/50">
          <div>
            <h3 className="text-xl font-playfair font-medium text-stone-900">Permissions: {user.name}</h3>
            <p className="text-sm text-stone-500">Configure access control for this staff member</p>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-white border border-stone-200 flex items-center justify-center hover:bg-stone-50">
            <Plus className="w-4 h-4 text-stone-500 rotate-45" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {Object.entries(schema).map(([resKey, resConfig]) => (
            <Card key={resKey} className="overflow-hidden border-stone-200">
              <div className="px-4 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                <span className="font-medium text-stone-900">{resConfig.label}</span>
              </div>
              <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {resConfig.actions.map(action => (
                  <div key={action} className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-stone-50">
                    <span className="text-sm text-stone-700 capitalize">{action}</span>
                    <Switch 
                      checked={!!perms[resKey]?.[action]} 
                      onCheckedChange={() => togglePerm(resKey, action)} 
                    />
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>

        <div className="p-6 border-t border-stone-200 bg-white flex justify-end gap-3 shrink-0">
          <Button variant="outline" onClick={onClose} className="h-10">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="h-10 bg-stone-900 text-white gap-2 px-6">
            {saving ? 'Saving...' : <><Save className="w-4 h-4"/> Save Permissions</>}
          </Button>
        </div>
      </motion.div>
    </>
  );
}

// Quick dummy icon
function Save(props) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>;
}
